import React from "react";
import { Handle, Position } from "reactflow";
import { Box, Typography, Chip, Tooltip, IconButton } from "@mui/material";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import ChatbotSlotTooltipContent from "./ChatbotSlotTooltipContent";
import { slotStepName } from "../../utils/chatbotNavigationActions";

/**
 * Nó do fluxo do ChatBot (canvas React Flow).
 */
const ChatbotNode = ({ data, selected }) => {
  const isExcecao = data?.identificador === "Exceção";
  const isInicio = !!data?.isFlowStart || data?.identificador === "Início";
  const isGlobal = !!data?.global;
  const ignoresGlobal = !!data?.ignorarglobal;
  const isSimulatorActive = !!data?.isSimulatorActive;
  const hasButtons = Array.isArray(data?.botoes) ? data.botoes.length > 0 : false;
  const hasChildren = !!data?.hasChildren;
  const isCollapsed = !!data?.isCollapsed;
  const hiddenCount = Number(data?.hiddenDescendantCount) || 0;
  const label = slotStepName(data);
  const fullPreview = data?.resposta ? String(data.resposta).replace(/\n/g, " ").trim() : "";
  const preview = fullPreview ? fullPreview.slice(0, 56) + (fullPreview.length > 56 ? "…" : "") : null;

  const handleToggleCollapse = (e) => {
    e.stopPropagation();
    e.preventDefault();
    data?.onToggleCollapse?.(String(data.id));
  };

  const content = (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          minWidth: 220,
          maxWidth: 300,
          p: 2,
          bgcolor: selected ? "primary.light" : isSimulatorActive ? "success.light" : "background.paper",
          color: selected ? "primary.contrastText" : isSimulatorActive ? "success.contrastText" : "text.primary",
          borderRadius: 2,
          border: "2px solid",
          borderColor: selected
            ? "primary.main"
            : isSimulatorActive
              ? "success.main"
              : isGlobal
                ? "primary.main"
                : "divider",
          borderStyle: isGlobal && !selected && !isSimulatorActive ? "dashed" : "solid",
          boxShadow: selected || isSimulatorActive ? 4 : 2,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
        }}
      >
        <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, left: -2 }} />
        <Box display="flex" alignItems="center" gap={1} mb={0.75}>
          {isExcecao ? (
            <SmartToyOutlined sx={{ fontSize: 20, opacity: 0.9, flexShrink: 0 }} />
          ) : (
            <QuestionAnswerOutlined sx={{ fontSize: 20, opacity: 0.9, flexShrink: 0 }} />
          )}
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ minWidth: 0, flex: 1 }} title={label}>
            {label}
          </Typography>
          {isCollapsed && hiddenCount > 0 ? (
            <Chip
              label={`+${hiddenCount}`}
              size="small"
              sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }}
              title={`${hiddenCount} subpasso(s) oculto(s)`}
            />
          ) : null}
          {isExcecao && (
            <Chip label="Exceção" size="small" sx={{ height: 18, fontSize: "0.7rem", flexShrink: 0 }} />
          )}
          {isInicio && (
            <Chip label="Início" size="small" color="primary" sx={{ height: 18, fontSize: "0.7rem", flexShrink: 0 }} />
          )}
          {isSimulatorActive && !selected ? (
            <Chip label="Simulador" size="small" color="success" sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }} />
          ) : null}
          {isGlobal && !isExcecao && !isInicio && (
            <Chip label="Global" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: "0.7rem", flexShrink: 0 }} />
          )}
          {ignoresGlobal && !isExcecao && (
            <Chip label="Ignora globais" size="small" sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }} />
          )}
        </Box>
        {preview && (
          <Typography variant="caption" display="block" sx={{ opacity: 0.85, lineHeight: 1.4 }} noWrap title={fullPreview}>
            {preview}
          </Typography>
        )}
        {hasButtons && (
          <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mt: 0.25 }}>
            {data.botoes.length} botão(ões)
          </Typography>
        )}
        <Handle type="source" position={Position.Right} style={{ width: 10, height: 10, right: -2 }} />
      </Box>

      {hasChildren ? (
        <Tooltip title={isCollapsed ? "Expandir subpassos" : "Recolher subpassos"} placement="right">
          <IconButton
            size="small"
            onClick={handleToggleCollapse}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              top: "50%",
              right: -18,
              transform: "translateY(-50%)",
              width: 28,
              height: 28,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              border: "2px solid",
              borderColor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            {isCollapsed ? <Add sx={{ fontSize: 16 }} /> : <Remove sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );

  return (
    <Tooltip
      title={<ChatbotSlotTooltipContent slot={data} />}
      placement="top"
      enterDelay={300}
      leaveDelay={80}
      arrow
      disableInteractive={false}
      slotProps={{
        tooltip: {
          sx: { maxWidth: 400, p: 1.25 },
        },
      }}
    >
      {content}
    </Tooltip>
  );
};

export default ChatbotNode;
